/*
 * Matching add-on (CRO) — resolves a product's matching counterpart and lets the
 * customer add it (with its own size) in one tap, with a real, computed saving.
 *
 * Matching is name-based (see parseTitle) with a manual metafield override.
 * The discount is ENFORCED by a Shopify discount Function keyed on the
 * `_matching_addon` line-item property — this script only adds the line + property
 * and shows the same saving the Function applies. It never fakes a price.
 */
(function () {
  'use strict';

  var TYPES = ['CHAIN', 'NECKLACE', 'PENDANT', 'BRACELET'];
  var NECK_TYPES = ['CHAIN', 'NECKLACE', 'PENDANT'];
  // Longest phrases first so "ROSE GOLD" wins over "GOLD".
  var FINISHES = ['ROSE GOLD', 'WHITE GOLD', 'BLACK GOLD', 'YELLOW GOLD', 'GOLD', 'SILVER', 'PLATINUM'];

  function normalize(str) {
    return (str || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // strip diacritics: AMORÉ / AMORÈ -> AMORE
      .replace(/\s+/g, ' ')
      .trim();
  }

  function detectFinish(afterStr) {
    for (var i = 0; i < FINISHES.length; i++) {
      var f = FINISHES[i];
      var re = new RegExp('(^| )' + f.replace(/ /g, '\\s') + '( |$)');
      if (re.test(afterStr)) return f;
    }
    return '';
  }

  // -> { primary, type, finish } or null when no jewelry TYPE token is present.
  function parseTitle(title) {
    var n = normalize(title);
    if (!n) return null;
    var tokens = n.split(' ');
    var typeIdx = -1;
    var type = null;
    for (var i = 0; i < tokens.length; i++) {
      if (TYPES.indexOf(tokens[i]) !== -1) {
        typeIdx = i;
        type = tokens[i];
        break;
      }
    }
    if (typeIdx === -1) return null;
    var primary = tokens.slice(0, typeIdx).join(' ');
    if (!primary) return null;
    var after = tokens.slice(typeIdx + 1).join(' ');
    return { primary: primary, type: type, finish: detectFinish(after) };
  }

  function counterpartTypesFor(type) {
    if (NECK_TYPES.indexOf(type) !== -1) return ['BRACELET'];
    if (type === 'BRACELET') return ['CHAIN', 'NECKLACE']; // prefer CHAIN, fall back to NECKLACE
    return null; // RING / EARRINGS / ANKLET / SET / ... -> no match
  }

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') return '';
    var value = '';
    var fmt = format || '${{amount}}';
    function defaultTo(v, p) { return parseInt(v, 10) === 0 ? p : v; }
    function thousands(num, sep) {
      return num.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (sep || ','));
    }
    function withDecimals(num, precision, decSep, thouSep) {
      precision = defaultTo(precision, 2);
      decSep = decSep || '.';
      var amount = (num / 100.0).toFixed(precision);
      var parts = amount.split('.');
      return thousands(parts[0], thouSep) + (parts[1] ? decSep + parts[1] : '');
    }
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount': value = withDecimals(cents, 2, '.', ','); break;
      case 'amount_no_decimals': value = withDecimals(cents, 0, '.', ','); break;
      case 'amount_with_comma_separator': value = withDecimals(cents, 2, ',', '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = withDecimals(cents, 0, ',', '.'); break;
      case 'amount_with_space_separator': value = withDecimals(cents, 2, ',', ' '); break;
      case 'amount_no_decimals_with_space_separator': value = withDecimals(cents, 0, '', ' '); break;
      default: value = withDecimals(cents, 2, '.', ',');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function toCents(amount) {
    return Math.round(parseFloat(amount) * 100);
  }

  customElements.define(
    'matching-addon',
    class MatchingAddon extends HTMLElement {
      constructor() {
        super();
        this.resolved = null;     // { product, finishOptionIndex }
        this.selection = null;    // array of selected option values aligned to product.options
        this.bound = false;
      }

      connectedCallback() {
        try {
          this.config = JSON.parse(this.querySelector('.matching-addon__config').textContent);
        } catch (e) {
          return;
        }
        this.sectionId = this.dataset.sectionId;
        this.checkbox = this.querySelector('[data-addon-checkbox]');
        this.reveal = this.querySelector('[data-addon-reveal]');
        this.labelEl = this.querySelector('[data-addon-label]');
        this.savingEl = this.querySelector('[data-addon-saving]');
        this.pillsEl = this.querySelector('[data-addon-pills]');
        this.sizesLegend = this.querySelector('[data-addon-sizes-legend]');
        this.messageEl = this.querySelector('[data-addon-message]');
        this.srStatus = this.querySelector('.matching-addon__sr-status');
        this.imageEl = this.querySelector('[data-addon-image]');
        this.productTitleEl = this.querySelector('[data-addon-product-title]');

        this.current = parseTitle(this.config.title);
        this.resolve(); // non-blocking; reveals block only on success
      }

      // --- Resolution -------------------------------------------------------
      async resolve() {
        try {
          var product = this.config.override
            ? await this.fetchProductJson(this.config.override.handle)
            : await this.resolveByName();
          if (!product) return;
          if (!product.available) return; // entirely out of stock -> render nothing
          this.setupUI(product);
        } catch (e) {
          /* fail silent: leave block hidden */
        }
      }

      async resolveByName() {
        if (!this.current) return null;
        var cps = counterpartTypesFor(this.current.type);
        if (!cps) return null;

        for (var i = 0; i < cps.length; i++) {
          var counterpartType = cps[i];
          var q = [this.current.primary, counterpartType, this.current.finish].filter(Boolean).join(' ');
          var url =
            '/search/suggest.json?q=' +
            encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=10';
          var res = await fetch(url);
          if (!res.ok) continue;
          var data = await res.json();
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];

          var pick = this.pickCounterpart(products, counterpartType);
          if (pick) {
            var full = await this.fetchProductJson(pick.handle);
            if (full && full.available) return full;
          }
        }
        return null;
      }

      // Re-parse every suggestion's title and keep only exact PRIMARY + counterpartType matches.
      pickCounterpart(products, counterpartType) {
        var self = this;
        var parsed = products
          .map(function (p) {
            return { p: p, t: parseTitle(p.title) };
          })
          .filter(function (x) {
            return (
              x.t &&
              x.t.primary === self.current.primary &&
              x.t.type === counterpartType
            );
          });
        if (!parsed.length) return null;

        if (self.current.finish) {
          var exact = parsed.filter(function (x) { return x.t.finish === self.current.finish; });
          if (exact.length) return exact[0].p;       // same finish preferred
          return parsed[0].p;                          // fall back: ignore finish
        }
        return parsed[0].p;                            // no finish on current -> primary+type only
      }

      async fetchProductJson(handle) {
        if (!handle) return null;
        var res = await fetch('/products/' + handle + '.js');
        if (!res.ok) return null;
        return res.json();
      }

      // --- UI ---------------------------------------------------------------
      setupUI(product) {
        this.resolved = { product: product };

        var parsedCp = parseTitle(product.title);
        var cpType = parsedCp ? parsedCp.type : (product.type || '').toUpperCase();
        var nice = cpType ? cpType.charAt(0) + cpType.slice(1).toLowerCase() : 'Item';
        this.counterpartType = cpType;
        this.labelEl.textContent = 'Add Matching ' + nice;
        this.checkbox.setAttribute('aria-label', 'Add matching ' + nice.toLowerCase());
        this.productTitleEl.textContent = product.title;
        if (product.featured_image) {
          this.imageEl.src = product.featured_image;
          this.imageEl.alt = product.title;
        } else {
          this.imageEl.remove();
        }

        // Identify the finish/colour option so we can auto-match it and hide it.
        this.finishOptionIndex = this.detectFinishOptionIndex(product);
        this.selection = product.options.map(function () { return null; });

        // Auto-select finish = main product's finish.
        if (this.finishOptionIndex > -1) {
          var values = this.optionValues(product, this.finishOptionIndex);
          var want = this.current && this.current.finish ? this.current.finish : '';
          var chosen = null;
          for (var i = 0; i < values.length; i++) {
            if (normalize(values[i]) === want) { chosen = values[i]; break; }
          }
          if (!chosen) chosen = this.firstAvailableFinish(product) || values[0];
          this.selection[this.finishOptionIndex] = chosen;
        }

        this.renderSizePills(product);
        this.preselectDefaultSize(product);
        this.updatePrice();
        this.bindEvents();

        this.hidden = false; // reveal the toggle row (block was display:none)
      }

      detectFinishOptionIndex(product) {
        for (var i = 0; i < product.options.length; i++) {
          var vals = this.optionValues(product, i);
          var finishHits = vals.filter(function (v) {
            return FINISHES.indexOf(normalize(v)) !== -1;
          });
          if (vals.length && finishHits.length >= Math.ceil(vals.length / 2)) return i;
        }
        return -1;
      }

      optionValues(product, idx) {
        // product.options on the .js payload is an array of names; values come from variants.
        var seen = [];
        product.variants.forEach(function (v) {
          var val = v.options[idx];
          if (val != null && seen.indexOf(val) === -1) seen.push(val);
        });
        return seen;
      }

      firstAvailableFinish(product) {
        var idx = this.finishOptionIndex;
        var found = null;
        product.variants.forEach(function (v) {
          if (!found && v.available) found = v.options[idx];
        });
        return found;
      }

      // Render a pill group for every non-finish option (size, and width/mm if present).
      // /products/<handle>.js may return options as strings OR as objects ({name,position,values}).
      // Always extract a display name safely so the legend never prints "[object Object]".
      optionName(opt) {
        return (typeof opt === 'object' && opt !== null) ? opt.name : opt;
      }

      renderSizePills(product) {
        this.pillsEl.innerHTML = '';
        var self = this;
        var groups = [];
        product.options.forEach(function (opt, idx) {
          if (idx === self.finishOptionIndex) return;
          groups.push({ name: self.optionName(opt), idx: idx });
        });

        this.sizesLegend.textContent = groups.map(function (g) { return g.name; }).join(' · ') || 'Size';

        groups.forEach(function (g) {
          var wrap = document.createElement('div');
          wrap.className = 'matching-addon__pill-group';
          wrap.dataset.optionIndex = g.idx;
          self.optionValues(product, g.idx).forEach(function (val, vi) {
            var id = 'ma-opt-' + self.sectionId + '-' + g.idx + '-' + vi;
            var available = self.valueAvailable(product, g.idx, val);
            var pill = document.createElement('div');
            pill.className = 'matching-addon__pill' + (available ? '' : ' matching-addon__pill--soldout');
            var input = document.createElement('input');
            input.type = 'radio';
            input.name = 'ma-opt-' + self.sectionId + '-' + g.idx;
            input.id = id;
            input.value = val;
            input.dataset.optionIndex = g.idx;
            if (!available) input.disabled = true;
            var label = document.createElement('label');
            label.setAttribute('for', id);
            label.textContent = val;
            input.addEventListener('change', function () {
              self.selection[g.idx] = val;
              self.refreshAvailability();
              self.updatePrice();
            });
            pill.appendChild(input);
            pill.appendChild(label);
            wrap.appendChild(pill);
          });
          self.pillsEl.appendChild(wrap);
        });
      }

      // A value is purchasable if some available variant has it, holding the finish fixed.
      valueAvailable(product, idx, val) {
        var finishIdx = this.finishOptionIndex;
        var finishVal = finishIdx > -1 ? this.selection[finishIdx] : null;
        return product.variants.some(function (v) {
          if (!v.available) return false;
          if (v.options[idx] !== val) return false;
          if (finishIdx > -1 && finishVal != null && v.options[finishIdx] !== finishVal) return false;
          return true;
        });
      }

      refreshAvailability() {
        var self = this;
        var product = this.resolved.product;
        this.pillsEl.querySelectorAll('.matching-addon__pill-group').forEach(function (group) {
          var idx = parseInt(group.dataset.optionIndex, 10);
          group.querySelectorAll('.matching-addon__pill').forEach(function (pill) {
            var input = pill.querySelector('input');
            var ok = self.valueAvailable(product, idx, input.value);
            input.disabled = !ok;
            pill.classList.toggle('matching-addon__pill--soldout', !ok);
            if (!ok && input.checked) {
              input.checked = false;
              self.selection[idx] = null;
            }
          });
        });
      }

      preselectDefaultSize(product) {
        var self = this;
        var defaultSize =
          this.config.category === 'NECK'
            ? this.config.defaultSizeWrist // counterpart of a NECK item is a WRIST item
            : this.config.defaultSizeNeck;
        this.pillsEl.querySelectorAll('.matching-addon__pill-group').forEach(function (group) {
          var idx = parseInt(group.dataset.optionIndex, 10);
          if (self.selection[idx]) return;
          var inputs = Array.prototype.slice.call(group.querySelectorAll('input'));
          var pick = null;
          if (defaultSize) {
            pick = inputs.find(function (inp) {
              return !inp.disabled && normalize(inp.value) === normalize(defaultSize);
            });
          }
          if (!pick) pick = inputs.find(function (inp) { return !inp.disabled; });
          if (pick) {
            pick.checked = true;
            self.selection[idx] = pick.value;
          }
        });
      }

      getSelectedVariant() {
        var sel = this.selection;
        return this.resolved.product.variants.find(function (v) {
          return sel.every(function (val, i) { return val == null || v.options[i] === val; });
        });
      }

      // --- Pricing ----------------------------------------------------------
      computeAddonCents(standaloneCents) {
        if (this.config.addonPriceOverrideCents != null) return this.config.addonPriceOverrideCents;
        var d = this.config.discount || {};
        if (d.type === 'percentage' && d.value) {
          return Math.round((standaloneCents * (100 - d.value)) / 100);
        }
        if (d.type === 'fixed' && d.value) {
          return Math.max(0, standaloneCents - Math.round(d.value * 100));
        }
        return standaloneCents; // no discount configured
      }

      // Current MAIN product variant price (cents). Tracks the live selection: starts from the
      // Liquid-provided selected price, updated by the theme's variantChange event, and falls
      // back to the variant->price map keyed by the product form's current id.
      getMainPrice() {
        if (typeof this.mainPrice === 'number') return this.mainPrice;
        var map = this.config.mainVariantPrices || {};
        var idInput = this.productForm && this.productForm.querySelector('[name="id"]');
        var id = idInput && idInput.value;
        if (id && map[id] != null) return map[id];
        return this.config.mainVariantPrice || 0;
      }

      updatePrice() {
        var variant = this.getSelectedVariant();
        var fmt = this.config.moneyFormat;
        if (!variant) {
          this.savingEl.textContent = '';
          return;
        }
        // The "set" = main product variant + matched add-on variant. The discount is a flat
        // percentage off the COMBINED selling price (enforced as the same % off BOTH lines),
        // so the displayed saving equals exactly what the Function charges.
        var addonPrice = variant.price; // selected matched variant, cents
        var mainPrice = this.getMainPrice(); // selected main variant, cents
        var combined = mainPrice + addonPrice;
        var d = this.config.discount || {};
        var pct = d.type === 'percentage' && d.value ? d.value : 0;
        var saving = Math.round((combined * pct) / 100);

        // Show ONLY the saving, with the combined set's original price as a subtle strikethrough.
        if (saving > 0) {
          this.savingEl.innerHTML =
            '<strong>Save ' + formatMoney(saving, fmt) + ' with the set</strong>' +
            '<span class="matching-addon__value-note">' + formatMoney(combined, fmt) + '</span>';
        } else {
          this.savingEl.textContent = '';
        }

        if (this.checkbox.checked && this.srStatus) {
          var noun = (this.counterpartType || 'item').toLowerCase();
          this.srStatus.textContent = saving > 0
            ? 'Matching ' + noun + ' added — save ' + formatMoney(saving, fmt) + ' with the set'
            : 'Matching ' + noun + ' added';
        }
      }

      // --- Events -----------------------------------------------------------
      bindEvents() {
        var self = this;

        // Seed the main variant price, then keep it current via the theme's variantChange event
        // so the "combined set" saving recomputes when the customer changes the main variant.
        this.mainPrice = typeof this.config.mainVariantPrice === 'number' ? this.config.mainVariantPrice : undefined;
        if (typeof subscribe === 'function') {
          var evt = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) || 'variant-change';
          subscribe(evt, function (payload) {
            var d = payload && payload.data;
            if (!d || d.sectionId !== self.sectionId || !d.variant) return;
            if (typeof d.variant.price === 'number') {
              self.mainPrice = d.variant.price;
              self.updatePrice();
            }
          });
        }

        this.checkbox.checked = !!this.config.defaultOn;
        this.toggleReveal(this.checkbox.checked);
        this.setUpcartSkip(this.checkbox.checked);
        this.checkbox.addEventListener('change', function () {
          self.toggleReveal(self.checkbox.checked);
          self.setUpcartSkip(self.checkbox.checked);
          self.updatePrice();
        });

        // Intercept the product form's submit in the CAPTURE phase on the <product-form>
        // ancestor, so it runs BEFORE Dawn's own handler. UpCart's competing interceptor is
        // neutralized via setUpcartSkip() (see below), so neither adds a stray single item.
        this.productForm = document.querySelector('product-form[data-section-id="' + this.sectionId + '"]');
        if (this.productForm && !this.bound) {
          this.productForm.addEventListener('submit', this.onFormSubmit.bind(this), true);
          this.bound = true;
        }
      }

      // This store's cart is UpCart (AfterSell), not Dawn's native drawer. UpCart intercepts
      // add-to-cart (form submit, button click, and — in race-prevention mode — the network
      // call) and gates all of it behind window.upcartShouldSkipAddToCartInterceptor. While our
      // toggle is ON we force that flag true so UpCart stands down and we own the combined add;
      // we capture UpCart's own baseline once (post-init) and restore it when the toggle is OFF.
      setUpcartSkip(on) {
        try {
          if (on) {
            if (!this._upBaselineCaptured) {
              this._upBaseline = window.upcartShouldSkipAddToCartInterceptor;
              this._upBaselineCaptured = true;
            }
            window.upcartShouldSkipAddToCartInterceptor = true;
          } else if (this._upBaselineCaptured) {
            window.upcartShouldSkipAddToCartInterceptor = this._upBaseline;
          }
        } catch (e) {
          /* UpCart not present yet — harmless */
        }
      }

      toggleReveal(open) {
        if (open) {
          this.reveal.hidden = false;
          // next frame so the max-height transition runs
          requestAnimationFrame(function () { /* noop to flush */ });
          this.reveal.setAttribute('data-open', '');
        } else {
          this.reveal.removeAttribute('data-open');
          this.reveal.hidden = true;
        }
      }

      showMessage(text) {
        if (!this.messageEl) return;
        this.messageEl.textContent = text;
        this.messageEl.hidden = !text;
      }

      onFormSubmit(evt) {
        if (!this.checkbox.checked || !this.resolved) return; // OFF -> normal Dawn + UpCart flow

        // We take over: stop the event reaching Dawn's handler, and (belt-and-suspenders)
        // ensure UpCart's interceptor is disabled for this add even if order ever races.
        evt.preventDefault();
        evt.stopPropagation();
        if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
        try { window.upcartShouldSkipAddToCartInterceptor = true; } catch (e) {}

        this.addBoth(evt.target);
      }

      async addBoth(formEl) {
        var mainIdInput = formEl.querySelector('[name="id"]');
        var qtyInput = formEl.querySelector('[name="quantity"]');
        var mainId = mainIdInput && mainIdInput.value;
        var mainQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        if (!mainId) return;

        var items = [{ id: mainId, quantity: mainQty }];

        var variant = this.getSelectedVariant();
        if (!variant || !variant.available) {
          // Sold out at click -> add the main item only; never block the main add.
          this.showMessage('That matching size just sold out — adding your item only.');
        } else {
          this.showMessage('');
          items.push({
            id: variant.id,
            quantity: 1,
            properties: {
              _matching_addon: 'true',
              _matching_with: String(mainId),
            },
          });
        }

        var submitBtn = formEl.querySelector('[type="submit"]');
        var spinner = submitBtn && submitBtn.querySelector('.loading__spinner');
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        }

        try {
          var response = await this.cartAdd(items);
          if (response && response.status) {
            // Atomic batch failed (e.g. matched variant sold out between load and click).
            // Recover by adding the main item alone so the customer never loses it.
            this.showMessage('That matching size is no longer available — adding your item only.');
            response = await this.cartAdd([{ id: mainId, quantity: mainQty }]);
          }
        } catch (e) {
          this.showMessage('Could not add to cart. Please try again.');
        } finally {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          }
        }

        // Refresh + open the UpCart drawer (this store's cart). No native Dawn drawer exists,
        // and we never redirect to /cart.
        await this.openUpcart();
      }

      cartAdd(items) {
        return fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: items }),
        }).then(function (r) { return r.json(); });
      }

      async openUpcart() {
        try {
          if (typeof window.upcartRefreshCart === 'function') {
            var p = window.upcartRefreshCart();
            if (p && typeof p.then === 'function') await p;
          }
        } catch (e) {
          /* ignore */
        }
        try {
          if (typeof window.upcartOpenCart === 'function') {
            window.upcartOpenCart();
            return;
          }
        } catch (e) {
          /* ignore */
        }
        // Fallback only if UpCart's API is somehow unavailable: poke generic cart listeners.
        // Deliberately no window.location redirect.
        try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
      }
    }
  );
})();
